/**
 * `resolveWsUrl(location, isDev)` — dev vs prod WS URL selection.
 *
 * Contract (see wp7-client-ui-input.md § "WebSocket client"):
 *   • Prod (server serves the client statics): same-origin
 *     `ws(s)://${host}` — the port, if any, lives in `location.host`.
 *     This is the DEFAULT: on `https://pitgame.gg` the browser dials
 *     `wss://pitgame.gg` and the reverse proxy (Fly) forwards the
 *     upgrade to the server's internal port.
 *   • Dev (Vite dev server): the page is on Vite but the game server is a
 *     separate process at `:8080` on the same hostname, so hop the port.
 *     Same protocol family (`ws:`/`wss:`) as the page.
 *
 * "Dev" is a BUILD-TIME fact (`import.meta.env.DEV`, baked in by Vite),
 * not something sniffed from the URL at runtime (#767). The old heuristic
 * — "any port that isn't 8080 is dev" — was wrong on a real domain:
 * `https://pitgame.gg` has `location.port === ""` (443 is implicit), so it
 * took the dev branch and dialed the unreachable `wss://pitgame.gg:8080`.
 * Port-keyed dev detection can't be repaired either: `vite.config.ts` sets
 * `strictPort: false`, so the dev port shifts to 5174+ on collision.
 *
 * PURE. Takes a `URL`-like input plus an explicit dev flag; only the
 * `resolveWsUrlFromWindow` wrapper below touches `import.meta.env` and
 * `window.location`, so the unit tests can drive both branches directly.
 */

export interface UrlLike {
  readonly protocol: string;
  readonly hostname: string;
  readonly host: string;
  readonly port: string;
}

/** Port the game server listens on in dev. Matches WP5 dev command. */
export const SERVER_PORT = "8080" as const;

/**
 * Return the WebSocket URL the client should connect to.
 *
 * @param location  `window.location`-like page URL.
 * @param isDev     `true` only for a Vite dev-server bundle; production
 *                  bundles are same-origin.
 */
export function resolveWsUrl(location: UrlLike, isDev = false): string {
  const wsProto = location.protocol === "https:" ? "wss:" : "ws:";
  if (isDev) {
    // Dev: browser is on Vite; hop to :8080 on the same hostname.
    return `${wsProto}//${location.hostname}:${SERVER_PORT}`;
  }
  // Prod: same-origin (server serves the client). Use the full host so
  // IPv6 literals and explicit non-default ports survive; an apex domain
  // on 443/80 has no port in `host` and correctly stays same-origin.
  return `${wsProto}//${location.host}`;
}

/**
 * Convenience for the calling code: resolve against `window.location`,
 * with the dev flag Vite baked into this bundle.
 * Returns the empty string in non-browser contexts (tests).
 */
export function resolveWsUrlFromWindow(): string {
  if (typeof window === "undefined") return "";
  return resolveWsUrl(window.location, import.meta.env.DEV);
}
